import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Dialog, Transition, Combobox } from "@headlessui/react";
import {
  XMarkIcon,
  ChevronUpDownIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import debounce from "lodash/debounce";
import { searchProvider } from "../../api/api-methods";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function mapSearchResult(provider) {
  const id = String(provider.Id || provider.id || "");
  const city = provider.city || "";
  const state = provider.state || "";
  const capabilities = (provider.provider_capabilities || provider.capabilities || [])
    .map((cap) => cap.rfi_name || cap.name)
    .filter(Boolean);
  return {
    id,
    name: provider.name || "",
    type: provider.provider_type || null,
    logoUrl: provider.logo || provider.Logo || null,
    primaryCityState: [city, state].filter(Boolean).join(", "),
    primaryCountry: provider.country || null,
    serviceCategories: capabilities,
  };
}

function getDefaultListName(existingNames = []) {
  let counter = 1;
  let name = `My Vendor List #${counter}`;
  const nameSet = new Set(existingNames.map((entry) => entry.toLowerCase()));
  while (nameSet.has(name.toLowerCase())) {
    counter += 1;
    name = `My Vendor List #${counter}`;
  }
  return name;
}

export default function CreateVendorListModal({
  isOpen,
  onClose,
  onCreateList,
  existingListNames = [],
  initialVendors = [],
  isSubmitting = false,
}) {
  const [listName, setListName] = useState("");
  const [notes, setNotes] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [createdListName, setCreatedListName] = useState("");
  const [vendorQuery, setVendorQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedVendors, setSelectedVendors] = useState([]);

  const debounceSearch = useCallback(
    debounce((text) => setDebouncedQuery(text), 300),
    []
  );

  useEffect(() => {
    if (!isOpen) return;
    setSelectedVendors(initialVendors || []);
    setListName("");
    setNotes("");
    setShowSuccess(false);
    setVendorQuery("");
    setDebouncedQuery("");
  }, [isOpen, initialVendors]);

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;

    const runSearch = async () => {
      if (debouncedQuery.trim().length < 2) {
        setSearchResults([]);
        setSearchLoading(false);
        return;
      }
      setSearchLoading(true);
      try {
        const data = await searchProvider(debouncedQuery.trim());
        if (!cancelled) {
          setSearchResults(Array.isArray(data) ? data.map(mapSearchResult) : []);
        }
      } catch {
        if (!cancelled) setSearchResults([]);
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    };

    runSearch();
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, isOpen]);

  const selectedIds = useMemo(
    () => new Set(selectedVendors.map((vendor) => String(vendor.id))),
    [selectedVendors]
  );

  const filteredResults = useMemo(
    () => searchResults.filter((vendor) => !selectedIds.has(String(vendor.id))),
    [searchResults, selectedIds]
  );

  const defaultName = useMemo(
    () => getDefaultListName(existingListNames),
    [existingListNames]
  );

  const handleSelectVendor = (vendor) => {
    if (!vendor || selectedIds.has(String(vendor.id))) return;
    setSelectedVendors((prev) => [...prev, vendor]);
    setVendorQuery("");
  };

  const handleRemoveVendor = (vendorId) => {
    setSelectedVendors((prev) => prev.filter((vendor) => String(vendor.id) !== String(vendorId)));
  };

  const handleCreate = async () => {
    const finalName = listName.trim() || defaultName;
    await onCreateList?.({
      name: finalName,
      notes: notes.trim(),
      vendors: selectedVendors,
    });
    setCreatedListName(finalName);
    setShowSuccess(true);
  };

  const handleClose = () => {
    setListName("");
    setNotes("");
    setSelectedVendors([]);
    setVendorQuery("");
    setShowSuccess(false);
    onClose?.();
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[9999]" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-visible rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="absolute right-0 top-0 pr-4 pt-4">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500"
                    onClick={handleClose}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                <div className="mt-3 w-full sm:mt-0 sm:text-left">
                  {showSuccess ? (
                    <>
                      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                        <CheckCircleIcon className="h-6 w-6 text-green-600" aria-hidden="true" />
                      </div>
                      <Dialog.Title className="text-center text-base font-semibold text-gray-900">
                        List Created Successfully!
                      </Dialog.Title>
                      <p className="mt-4 text-center text-sm text-gray-500">
                        &ldquo;{createdListName}&rdquo; has been created
                        {selectedVendors.length
                          ? ` with ${selectedVendors.length} vendor${
                              selectedVendors.length === 1 ? "" : "s"
                            }`
                          : ""}
                        .
                      </p>
                      <div className="mt-5 flex justify-center gap-3">
                        <button
                          type="button"
                          className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500"
                          onClick={handleClose}
                        >
                          Done
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <Dialog.Title className="text-base font-semibold text-gray-900">
                        Create Vendor List
                      </Dialog.Title>
                      <div className="mt-4 space-y-4">
                        <div>
                          <label
                            htmlFor="create-list-name"
                            className="block text-sm font-medium text-gray-700"
                          >
                            List Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            id="create-list-name"
                            type="text"
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            placeholder={defaultName}
                            value={listName}
                            onChange={(event) => setListName(event.target.value)}
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            Leave blank to use: {defaultName}
                          </p>
                        </div>

                        <div>
                          <label
                            htmlFor="create-list-notes"
                            className="block text-sm font-medium text-gray-700"
                          >
                            Notes (Optional)
                          </label>
                          <textarea
                            id="create-list-notes"
                            rows={3}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            placeholder="Add notes about this vendor list…"
                            value={notes}
                            onChange={(event) => setNotes(event.target.value)}
                          />
                        </div>

                        <div>
                          <label className="mb-1 block text-sm font-medium text-gray-700">
                            Initial Membership (Optional)
                          </label>
                          <Combobox as="div" value={null} onChange={handleSelectVendor} className="relative">
                            <div className="relative">
                              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                              </div>
                              <Combobox.Input
                                className="w-full rounded-md border-0 bg-white py-2 pl-9 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                                onChange={(event) => {
                                  setVendorQuery(event.target.value);
                                  debounceSearch(event.target.value);
                                }}
                                displayValue={() => vendorQuery}
                                placeholder="Search vendors by name…"
                                autoComplete="off"
                              />
                              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2">
                                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                              </Combobox.Button>
                            </div>
                            {filteredResults.length > 0 ? (
                              <Combobox.Options className="absolute z-[10000] mt-1 max-h-48 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 sm:text-sm">
                                {filteredResults.map((vendor) => (
                                  <Combobox.Option
                                    key={vendor.id}
                                    value={vendor}
                                    className={({ active }) =>
                                      classNames(
                                        "relative cursor-pointer select-none py-2 pl-3 pr-9",
                                        active ? "bg-indigo-600 text-white" : "text-gray-900"
                                      )
                                    }
                                  >
                                    <span className="block truncate font-medium">{vendor.name}</span>
                                  </Combobox.Option>
                                ))}
                              </Combobox.Options>
                            ) : null}
                          </Combobox>

                          {selectedVendors.length > 0 ? (
                            <div className="mt-2 flex max-h-32 flex-wrap gap-2 overflow-y-auto">
                              {selectedVendors.map((vendor) => (
                                <span
                                  key={vendor.id}
                                  className="inline-flex items-center gap-x-1.5 rounded-md bg-indigo-600 px-2 py-1 text-xs font-medium text-white"
                                >
                                  {vendor.name}
                                  <button
                                    type="button"
                                    className="-mr-1 ml-1 h-3.5 w-3.5 rounded-sm hover:bg-indigo-800/20"
                                    onClick={() => handleRemoveVendor(vendor.id)}
                                  >
                                    <span className="sr-only">Remove {vendor.name}</span>×
                                  </button>
                                </span>
                              ))}
                            </div>
                          ) : null}
                          <p className="mt-1 text-xs text-gray-500">
                            {searchLoading
                              ? "Searching…"
                              : `${selectedVendors.length} vendor${
                                  selectedVendors.length === 1 ? "" : "s"
                                } selected`}
                          </p>
                        </div>
                      </div>

                      <div className="mt-5 flex justify-end gap-3">
                        <button
                          type="button"
                          className="rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                          onClick={handleClose}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          disabled={isSubmitting}
                          className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-50"
                          onClick={handleCreate}
                        >
                          {isSubmitting ? "Creating…" : "Create List"}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
